import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import urlJoin from 'url-join';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { hasOwnProperty } from 'utils';
import { SiteNavNested } from 'msp-components';
import Error404 from 'components/Error404';
import Wrapper from 'components/Wrapper';
import Title from 'components/Title';
import templateOptionsPropTypes from 'components/templateOptionsPropTypes';

const WebsiteWrapper = ({ project, navConfig, menuItems, templateOptions, sections, items, history, location }) => {
  const [containerSize, setContainerSize] = useState({});
  const handleResize = (width, height) => setContainerSize({ width, height });

  const navItems = useMemo(() => {
    const formatData = data => data.map(({ uid, label, external, url, children }) => ({
      id: uid,
      label,
      containerOnly: external && !url,
      subItems: formatData(children),
    }));
    return formatData(navConfig || []);
  }, [navConfig]);

  const { titlePaths, routePaths, navItemsById } = useMemo(() => {
    const titlePaths = {};
    let routePaths = [];
    const navItemsById = {};

    /* eslint-disable-next-line */
    const processPaths = (items, root = '/') => items.forEach(item => {
      const { uid, external, slug, target, children } = item;
      const itemPath = urlJoin(root, slug);
      navItemsById[uid] = { ...item, path: itemPath };
      if (!external && target) {
        routePaths.push([itemPath, target, uid]);
        if (!hasOwnProperty(titlePaths, target)) {
          titlePaths[target] = itemPath;
        }
      }
      processPaths(children || [], itemPath);
    });
    processPaths(navConfig || []);

    // Fill in any items not included in nav config
    sections.ordered.forEach(sId => {
      const section = sections.byId[sId];
      section.items.forEach(item => {
        if (!hasOwnProperty(titlePaths, item.uid)) {
          const path = `/${section.slug}/${item.urlSlug}`;
          routePaths.push([path, item.uid]);
          titlePaths[item.uid] = path;
        }
      });
    });

    // Sort paths by complexity, so that routes appear in proper priority order
    const parts = path => path.split('/').filter(s => !!s).length;
    routePaths = routePaths.sort((a, b) => parts(b[0]) - parts(a[0]));

    return { titlePaths, routePaths, navItemsById };
  }, [sections, navConfig]);

  const { itemsOrdered, itemsById } = sections.ordered.reduce((result, sId) => {
    const section = sections.byId[sId];
    section.items.forEach(item => {
      result.itemsOrdered.push(item.uid);
      result.itemsById[item.uid] = item;
    });
    return result;
  }, { itemsOrdered: [], itemsById: {} });

  const firstItemPath = titlePaths[itemsOrdered[0]];

  const activeRoute = routePaths.find(([path]) => {
    const pathToMatch = location.pathname === '/' ? firstItemPath : location.pathname;
    return pathToMatch === path || pathToMatch.startsWith(path + '/');
  });
  const activeItemId = activeRoute && activeRoute[1];
  const activeItem = !!activeItemId && items.byId[activeItemId];

  const activeNavItemId = activeRoute && activeRoute[2];

  const handleItemSelect = id => {
    const { external, path, url } = navItemsById[id];
    if (external) {
      if (url.startsWith('/')) {
        history.push(url);
      } else if (url) {
        window.open(url);
      }
    } else {
      history.push(path);
    }
  };

  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const handleMenuItemSelect = setActiveMenuItem;

  const routes = (
    <Route render={({ location }) => {
      // Remove trailing slash, preserving query params
      if (location.pathname.endsWith('/') && location.pathname !== '/') {
        return <Redirect to={location.pathname.slice(0, -1) + location.search} />;
      }

      // Check for global error state
      return location.state && location.state.is404 ? <Error404 /> : (
        <Switch>
          <Route
            exact
            path="/"
            render={props => {
              return (
                <Title
                  routeProps={props}
                  isHomepage
                  canonicalPath={firstItemPath}
                  containerWidth={containerSize.width}
                  project={project}
                  menuItems={menuItems}
                  data={itemsById[itemsOrdered[0]]}
                  onSelectMenuItem={handleMenuItemSelect}
                />
              );
            }}
          />
          {routePaths.map(([path, targetId, navItemId]) => (
            <Route
              key={navItemId}
              path={path}
              render={props => !!itemsById[targetId] && (
                <Title
                  routeProps={props}
                  containerWidth={containerSize.width}
                  project={project}
                  menuItems={menuItems}
                  data={itemsById[targetId]}
                  onSelectMenuItem={handleMenuItemSelect}
                />
              )}
            />
          ))}
          <Route component={Error404} />
        </Switch>
      );
    }} />
  );

  const navigation = typeof containerSize.width !== 'undefined' && (
    <SiteNavNested
      useMobileLayout={containerSize.width < 500}
      items={navItems}
      activeItemId={activeNavItemId}
      onSelectItem={handleItemSelect}
    />
  );

  return (
    <Wrapper
      project={project}
      titlePaths={titlePaths}
      itemsOrdered={itemsOrdered}
      itemsById={itemsById}
      isHomepage={location.pathname === '/'}
      templateOptions={templateOptions}
      navigation={navigation}
      activeItem={activeItem}
      activeMenuItem={activeMenuItem}
      onContainerResize={handleResize}
    >
      {routes}
    </Wrapper>
  );
};

const navItemShape = {
  label: PropTypes.string,
  slug: PropTypes.string,
  uid: PropTypes.string,
  target: PropTypes.string,
  external: PropTypes.bool,
  url: PropTypes.string,
};

navItemShape.children = PropTypes.arrayOf(PropTypes.shape(navItemShape));

WebsiteWrapper.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    content: PropTypes.string,
    isVisible: PropTypes.bool,
  })),
  templateOptions: PropTypes.shape(templateOptionsPropTypes),
  useCustomNav: PropTypes.bool,
  navConfig: PropTypes.arrayOf(PropTypes.shape(navItemShape)),
  project: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
    shortDescription: PropTypes.string,
    longDescription: PropTypes.string,
    socialImage: PropTypes.string,
    analyticsProvider: PropTypes.string,
    analyticsId: PropTypes.string,
    shareUrl: PropTypes.string,
    publisher: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      twitter: PropTypes.string,
      facebook: PropTypes.string,
    }),
  }),
  sections: PropTypes.shape({
    byId: PropTypes.object,
    ordered: PropTypes.arrayOf(PropTypes.string),
  }),
  items: PropTypes.shape({
    byId: PropTypes.object,
    bySection: PropTypes.object,
  }),
};

export default withRouter(WebsiteWrapper);
